import { useQuery, useMutation } from '@tanstack/react-query'
import type {
  TrainingRequest,
  TrainingGenerationResponse,
  ApiResponse,
  TrainingSession,
  Group,
  GroupMember,
  GroupSession
} from '../types/index'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import { supabase } from '../lib/supabase'

const API_BASE_URL = getApiBaseUrl()
const API_KEY = import.meta.env.VITE_API_KEY || 'public-512ff1a7ad51ef7290086ce8676f'

class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Response
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

class ApiService {
  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${API_BASE_URL}${endpoint}`
      
      const response = await fetch(url, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': API_KEY,
          ...options.headers,
        },
      })

      const data = await response.json()

      if (!response.ok) {
        throw new ApiError(
          data.error || 'API request failed',
          response.status,
          response
        )
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message,
      }
    } catch (error) {
      console.error('API Error:', error)
      
      if (error instanceof ApiError) {
        return {
          success: false,
          error: error.message,
        }
      }

      // Network or other errors
      return {
        success: false,
        error: 'Netzwerkfehler. Bitte prüfe deine Internetverbindung.',
      }
    }
  }

  // Health Check
  async checkHealth(): Promise<ApiResponse<{ status: string; timestamp: string }>> {
    return this.request('/health')
  }

  // Generate Training
  async generateTraining(request: TrainingRequest): Promise<ApiResponse<TrainingGenerationResponse>> {
    return this.request('/training/generate', {
      method: 'POST',
      body: JSON.stringify(request),
    })
  }

  // Save Training Session
  async saveTraining(session: TrainingSession): Promise<ApiResponse<{ id: string }>> {
    return this.request('/training/save', {
      method: 'POST',
      body: JSON.stringify(session),
    })
  }

  // Get Training History
  async getTrainingHistory(): Promise<ApiResponse<TrainingSession[]>> {
    return this.request('/training/history')
  }

  // Rate Training Session
  async rateTraining(sessionId: string, rating: number): Promise<ApiResponse<void>> {
    return this.request(`/training/${sessionId}/rate`, {
      method: 'POST',
      body: JSON.stringify({ rating }),
    })
  }

  // Get Coach Messages
  async getCoachMessage(context?: {
    level?: string
    exercise?: string
    progress?: number
  }): Promise<ApiResponse<{ message: string; thaiWisdom?: { thai: string; translation: string } }>> {
    const params = new URLSearchParams()
    if (context?.level) params.append('level', context.level)
    if (context?.exercise) params.append('exercise', context.exercise)
    if (context?.progress) params.append('progress', context.progress.toString())

    const endpoint = `/coach/message${params.toString() ? `?${params}` : ''}`
    return this.request(endpoint)
  }

  // Submit Training Feedback
  async submitFeedback(feedback: {
    session_id: string
    user_id: string
    session_feedback: any
    exercise_feedback: any[]
  }): Promise<ApiResponse<{ learning_applied: boolean; recommendations?: any }>> {
    return this.request('/training/feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    })
  }

  // Submit Trainer Feedback
  async submitTrainerFeedback(feedback: {
    trainer_id: string
    session_id: string
    client_id: string
    feedback_data: any
  }): Promise<ApiResponse<{ feedback_received: boolean; feedback_analysis?: any }>> {
    return this.request('/b2b/trainer-feedback', {
      method: 'POST',
      body: JSON.stringify(feedback),
    })
  }

  // ===== Group training (coach-only, role-gated server-side) =============

  // Group endpoints require the coach's identity, so attach the Supabase
  // access token (the public API key alone resolves to an athlete role).
  private async authedRequest<T>(endpoint: string, options: RequestInit = {}): Promise<ApiResponse<T>> {
    let token: string | undefined
    try {
      const { data } = await supabase.auth.getSession()
      token = data.session?.access_token
    } catch {
      token = undefined
    }
    return this.request<T>(endpoint, {
      ...options,
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    })
  }

  async listGroups(): Promise<ApiResponse<{ groups: Group[] }>> {
    return this.authedRequest('/groups')
  }

  async createGroup(payload: { name: string; group_type?: string; default_equipment?: string[] }): Promise<ApiResponse<{ group: Group }>> {
    return this.authedRequest('/groups', { method: 'POST', body: JSON.stringify(payload) })
  }

  async listGroupMembers(groupId: string): Promise<ApiResponse<{ members: GroupMember[] }>> {
    return this.authedRequest(`/groups/${groupId}/members`)
  }

  async addGroupMember(groupId: string, user_id: string): Promise<ApiResponse<{ member: GroupMember }>> {
    return this.authedRequest(`/groups/${groupId}/members`, { method: 'POST', body: JSON.stringify({ user_id }) })
  }

  async removeGroupMember(groupId: string, userId: string): Promise<ApiResponse<{ removed: boolean }>> {
    return this.authedRequest(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' })
  }

  async generateGroupSession(groupId: string, payload: { duration?: number } = {}): Promise<ApiResponse<{ session: GroupSession }>> {
    return this.authedRequest(`/groups/${groupId}/generate`, { method: 'POST', body: JSON.stringify(payload) })
  }

  async listGroupSessions(groupId: string): Promise<ApiResponse<{ sessions: GroupSession[] }>> {
    return this.authedRequest(`/groups/${groupId}/sessions`)
  }
}

// Singleton instance
export const apiService = new ApiService()

// React Query hooks
export const useApiHealth = () => {
  return useQuery({
    queryKey: ['api-health'],
    queryFn: () => apiService.checkHealth(),
    refetchInterval: 30000, // Check every 30 seconds
    retry: 3,
  })
}

export const useGenerateTraining = () => {
  return useMutation({
    mutationFn: (request: TrainingRequest) => apiService.generateTraining(request),
    retry: 1,
  })
}

export const useSaveTraining = () => {
  return useMutation({
    mutationFn: (session: TrainingSession) => apiService.saveTraining(session),
    retry: 2,
  })
}

export const useRateTraining = () => {
  return useMutation({
    mutationFn: ({ sessionId, rating }: { sessionId: string; rating: number }) =>
      apiService.rateTraining(sessionId, rating),
    retry: 1,
  })
}

export const useSubmitFeedback = () => {
  return useMutation({
    mutationFn: (feedback: {
      session_id: string
      user_id: string
      session_feedback: any
      exercise_feedback: any[]
    }) => apiService.submitFeedback(feedback),
    retry: 1,
  })
}

export const useSubmitTrainerFeedback = () => {
  return useMutation({
    mutationFn: (feedback: {
      trainer_id: string
      session_id: string
      client_id: string
      feedback_data: any
    }) => apiService.submitTrainerFeedback(feedback),
    retry: 1,
  })
}